import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Link as LinkIcon, RefreshCw, Upload, Search, X, Wand2, CheckSquare, Square, Receipt, Package, Truck, BarChart3, Archive, ArchiveRestore, Download } from "lucide-react";
import { toast } from "sonner";
import ImportClientsDialog from "@/components/ImportClientsDialog";
import { CLIENT_CATEGORIES, CATEGORY_LABEL, CATEGORY_SHORT } from "@/lib/clientCategories";
import GstVaultDialog, { VaultBadge } from "@/components/clients/GstVaultDialog";
import GstVaultBanner from "@/components/clients/GstVaultBanner";

const isGstClient = (c) => (c.categories || []).some((k) => k === "gst_monthly" || k === "gst_quarterly");

const ArchiveMeta = ({ c }) => (
  <div className="text-[10px] text-amber-800 font-normal mt-0.5" data-testid={`archive-meta-${c.id}`}>
    Archived {c.archived_at ? new Date(c.archived_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}{c.archived_by ? ` by ${c.archived_by}` : ""}{c.archive_reason ? ` · ${c.archive_reason}` : ""}
  </div>
);

const empty = { name: "", contact_person: "", email: "", phone: "", gstin: "", pan: "", notes: "", categories: [], other_services: "" };

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const archived = params.get("view") === "archived";
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [nature, setNature] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkCat, setBulkCat] = useState("gst_monthly");
  const [bulkMode, setBulkMode] = useState("add");
  const [bulkOther, setBulkOther] = useState("");
  const [vault, setVault] = useState({ allowed: false, items: {}, summary: null });
  const [vaultClient, setVaultClient] = useState(null);
  const [vaultFilter, setVaultFilter] = useState("");

  const loadVault = async () => { try { setVault((await api.get("/gst-vault/status")).data); } catch { /* non-privileged */ } };
  const load = async () => {
    const [list, cnt] = await Promise.all([
      api.get("/clients", { params: { category: category || undefined, q: q || undefined, archived: archived || undefined } }),
      api.get("/clients/category-counts"),
    ]);
    setItems(list.data); setCounts(cnt.data);
    loadVault();
  };
  const vaultStatus = (c) => vault.items[c.id]?.status || "missing";
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [category, q, archived]); // eslint-disable-line
  useEffect(() => { setNature(""); setSelected([]); }, [category, archived]);

  const visible = useMemo(() => {
    let list = (category === "other" && nature) ? items.filter(c => (c.other_services || "").trim() === nature) : items;
    if (vaultFilter) list = list.filter(c => isGstClient(c) && (vault.items[c.id]?.status || "missing") === vaultFilter);
    return list;
  }, [items, category, nature, vaultFilter, vault]);
  const natures = counts?.other_services || [];

  const toggleSel = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSel = visible.length > 0 && visible.every(c => selected.includes(c.id));
  const toggleAll = () => setSelected(allSel ? [] : visible.map(c => c.id));
  const applyBulk = async () => {
    try {
      const body = { client_ids: selected, categories: [bulkCat], mode: bulkMode };
      if (bulkCat === "other" && bulkOther.trim() && bulkMode !== "remove") body.other_services = bulkOther.trim();
      const { data } = await api.post("/clients/bulk-categorize", body);
      toast.success(`${data.modified} client(s) updated`, { description: `${bulkMode === "remove" ? "Removed" : bulkMode === "replace" ? "Set to" : "Added"} ${CATEGORY_LABEL[bulkCat]}` });
      setSelected([]); load();
    } catch (e) { toast.error("Bulk update failed", { description: e.response?.data?.detail || e.message }); }
  };

  const setCategory = (c) => { const p = new URLSearchParams(params); c ? p.set("category", c) : p.delete("category"); p.delete("view"); setParams(p); };
  const showArchived = () => { const p = new URLSearchParams(params); p.delete("category"); p.set("view", "archived"); setParams(p); };

  const archiveOne = async (c) => {
    const reason = window.prompt(`Deactivate ${c.name}?\nThey will move to the Archived list (no tasks, reminders or festival messages). Optional reason:`, "");
    if (reason === null) return;
    try { await api.post(`/clients/${c.id}/archive`, { client_ids: [c.id], reason }); toast.success(`${c.name} archived`, { description: "Restore any time from the Archived tab" }); load(); }
    catch (e) { toast.error("Archive failed", { description: e.response?.data?.detail || e.message }); }
  };
  const restoreOne = async (c) => {
    try { await api.post(`/clients/${c.id}/restore`); toast.success(`${c.name} is active again`); load(); }
    catch (e) { toast.error("Restore failed", { description: e.response?.data?.detail || e.message }); }
  };
  const bulkArchive = async () => {
    const reason = window.prompt(`Archive ${selected.length} selected client(s)? Optional reason:`, "");
    if (reason === null) return;
    try { const { data } = await api.post("/clients/bulk-archive", { client_ids: selected, reason }); toast.success(`${data.modified} client(s) archived`); setSelected([]); load(); }
    catch (e) { toast.error("Bulk archive failed", { description: e.response?.data?.detail || e.message }); }
  };
  const bulkRestore = async () => {
    try { const { data } = await api.post("/clients/bulk-restore", { client_ids: selected }); toast.success(`${data.modified} client(s) restored`); setSelected([]); load(); }
    catch (e) { toast.error("Bulk restore failed", { description: e.response?.data?.detail || e.message }); }
  };
  const exportArchived = () => {
    const rows = [["Name", "Contact", "Phone", "Email", "GSTIN", "PAN", "Archived on", "Reason", "Archived by"], ...visible.map(c => [c.name, c.contact_person, c.phone, c.email, c.gstin, c.pan, c.archived_at ? c.archived_at.slice(0, 10) : "", c.archive_reason, c.archived_by])];
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `archived-clients-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const save = async () => {
    try {
      if (editId) { await api.patch(`/clients/${editId}`, form); toast.success("Client updated"); }
      else { await api.post("/clients", form); toast.success("Client added"); }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const edit = (c) => { setForm({ ...empty, ...c, categories: c.categories || [] }); setEditId(c.id); setOpen(true); };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`); load();
  };
  const autoTag = async () => {
    const { data } = await api.post("/clients/auto-categorize");
    toast.success(`Auto-tagged ${data.tagged} client(s)`, { description: "Based on their existing tasks. Edit any client to correct." });
    load();
  };
  const toggleCat = (k) => setForm(f => ({ ...f, categories: f.categories.includes(k) ? f.categories.filter(x => x !== k) : [...f.categories, k] }));

  const portalUrl = (c) => c.portal_token ? `${window.location.origin}/portal/${c.portal_token}` : "";
  const copyPortal = async (c) => {
    const url = portalUrl(c);
    if (!url) { toast.error("No portal token — try refreshing"); return; }
    try { await navigator.clipboard.writeText(url); toast.success("Portal link copied", { description: url }); }
    catch { window.prompt("Copy the portal link:", url); }
  };
  const rotatePortal = async (c) => {
    if (!window.confirm(`Rotate portal link for ${c.name}? The old link will stop working.`)) return;
    await api.post(`/clients/${c.id}/rotate-portal-token`);
    toast.success("Portal link rotated");
    load();
  };

  const title = archived ? "Archived Clients" : category ? CATEGORY_LABEL[category] : "All Clients";

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Book of business</div>
          <h1 data-testid="clients-title" className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2">{title}</h1>
          {archived ? <p className="text-xs text-zinc-500 mt-1" data-testid="archived-hint">Deactivated clients. They get no tasks, reminders or festival messages and are hidden from regular lists. Restore any time to bring them back.</p> : null}
        </div>
        {isAdmin && archived ? (
          <Button data-testid="export-archived-btn" variant="outline" onClick={exportArchived} disabled={!visible.length} className="rounded-none border-zinc-300"><Download size={16} className="mr-1" /> Export list</Button>
        ) : null}
        {isAdmin && !archived ? (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="auto-tag-btn" variant="outline" onClick={autoTag} className="flex-1 sm:flex-initial rounded-none border-zinc-300" title="Tag untagged clients from their tasks">
              <Wand2 size={16} className="mr-1" /> Auto-tag
            </Button>
            <Button data-testid="import-clients-btn" variant="outline" onClick={() => setImportOpen(true)} className="flex-1 sm:flex-initial rounded-none border-zinc-300">
              <Upload size={16} className="mr-1" /> Import CSV
            </Button>
            <Button data-testid="new-client-btn" onClick={() => { setForm({ ...empty, categories: category ? [category] : [] }); setEditId(null); setOpen(true); }} className="flex-1 sm:flex-initial bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
              <Plus size={16} className="mr-1" /> Add Client
            </Button>
          </div>
        ) : null}
      </header>

      {/* Category tabs + search */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-1" data-testid="client-category-tabs">
          <Tab active={!category && !archived} onClick={() => setCategory("")} tid="cat-tab-all" label="All" count={counts?.all} />
          {CLIENT_CATEGORIES.map(c => (
            <Tab key={c.key} active={category === c.key && !archived} onClick={() => setCategory(c.key)} tid={`cat-tab-${c.key}`} label={c.label} count={counts?.[c.key]} />
          ))}
          <Tab active={archived} onClick={showArchived} tid="cat-tab-archived" label="Archived" count={counts?.archived} />
        </div>
        <div className="relative lg:ml-auto lg:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, contact, email, phone, GSTIN, PAN…" className="pl-9 pr-8 rounded-none" />
          {q ? <button data-testid="client-search-clear" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 rounded"><X size={14} /></button> : null}
        </div>
      </div>

      {category === "other" && natures.length ? (
        <div className="flex flex-wrap gap-1.5" data-testid="nature-chips">
          <Chip active={!nature} onClick={() => setNature("")} label="All natures" tid="nature-chip-all" />
          {natures.map(n => <Chip key={n} active={nature === n} onClick={() => setNature(n)} label={n} tid={`nature-chip-${n}`} />)}
        </div>
      ) : null}

      {vault.allowed ? <GstVaultBanner summary={vault.summary} active={vaultFilter} onFilter={setVaultFilter} /> : null}

      <ImportClientsDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <GstVaultDialog client={vaultClient} open={!!vaultClient} onOpenChange={(o) => { if (!o) setVaultClient(null); }} isAdmin={isAdmin} onChanged={loadVault} />

      {isAdmin && selected.length ? (
        <div data-testid="bulk-bar" className="sticky top-14 lg:top-0 z-20 bg-zinc-900 text-white p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 shadow-lg">
          <div className="text-xs font-semibold tabular" data-testid="bulk-count">{selected.length} selected</div>
          {archived ? (
            <div className="flex gap-2 sm:ml-auto">
              <button data-testid="bulk-restore-btn" onClick={bulkRestore} className="text-xs font-semibold px-3 py-1.5 bg-emerald-500 text-white hover:bg-emerald-400 transition-colors flex items-center gap-1"><ArchiveRestore size={13} /> Restore selected</button>
              <button data-testid="bulk-clear-btn" onClick={() => setSelected([])} className="text-xs px-3 py-1.5 border border-zinc-600 hover:bg-zinc-800 transition-colors">Clear</button>
            </div>
          ) : (<>
          <select data-testid="bulk-mode-select" value={bulkMode} onChange={e => setBulkMode(e.target.value)} className="bg-zinc-800 text-white text-xs px-2 py-1.5 border border-zinc-700">
            <option value="add">Add to</option>
            <option value="replace">Move to (replace)</option>
            <option value="remove">Remove from</option>
          </select>
          <select data-testid="bulk-category-select" value={bulkCat} onChange={e => setBulkCat(e.target.value)} className="bg-zinc-800 text-white text-xs px-2 py-1.5 border border-zinc-700">
            {CLIENT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {bulkCat === "other" && bulkMode !== "remove" ? (
            <input data-testid="bulk-other-input" value={bulkOther} onChange={e => setBulkOther(e.target.value)} placeholder="Nature (e.g. TDS)" className="bg-zinc-800 text-white text-xs px-2 py-1.5 border border-zinc-700 placeholder:text-zinc-500 w-36" />
          ) : null}
          <div className="flex gap-2 sm:ml-auto">
            <button data-testid="bulk-apply-btn" onClick={applyBulk} className="text-xs font-semibold px-3 py-1.5 bg-white text-zinc-900 hover:bg-zinc-200 transition-colors">Apply</button>
            <button data-testid="bulk-archive-btn" onClick={bulkArchive} className="text-xs font-semibold px-3 py-1.5 bg-amber-500 text-zinc-900 hover:bg-amber-400 transition-colors flex items-center gap-1"><Archive size={13} /> Archive</button>
            <button data-testid="bulk-clear-btn" onClick={() => setSelected([])} className="text-xs px-3 py-1.5 border border-zinc-600 hover:bg-zinc-800 transition-colors">Clear</button>
          </div>
          </>)}
        </div>
      ) : null}

      {/* Mobile: stacked cards */}
      <div className="sm:hidden space-y-2" data-testid="clients-mobile-list">
        {visible.map(c => (
          <div key={c.id} data-testid={`client-card-${c.id}`} className={`bg-white border p-3 ${selected.includes(c.id) ? "border-zinc-900" : "border-zinc-200"}`}>
            <div className="flex items-start justify-between gap-2">
              {isAdmin ? (
                <button data-testid={`select-client-${c.id}-m`} onClick={() => toggleSel(c.id)} className="p-1 -ml-1 mt-0.5 shrink-0">
                  {selected.includes(c.id) ? <CheckSquare size={16} /> : <Square size={16} className="text-zinc-400" />}
                </button>
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{c.name}</div>
                {c.contact_person ? <div className="text-[11px] text-zinc-500 truncate">{c.contact_person}</div> : null}
                {archived ? <ArchiveMeta c={c} /> : null}
                <CatChips c={c} />
                {vault.allowed && isGstClient(c) ? (
                  <button data-testid={`gst-vault-btn-${c.id}-m`} onClick={() => setVaultClient(c)} className="mt-1.5 block"><VaultBadge status={vaultStatus(c)} daysLeft={vault.items[c.id]?.days_left} tid={`gst-vault-badge-${c.id}-m`} /></button>
                ) : null}
              </div>
              {isAdmin ? (
                <div className="flex gap-0.5 shrink-0">
                  {archived ? <button data-testid={`restore-client-${c.id}-m`} onClick={() => restoreOne(c)} title="Restore to active" className="p-1.5 hover:bg-emerald-50 rounded"><ArchiveRestore size={14} className="text-emerald-700" /></button>
                    : <button data-testid={`archive-client-${c.id}-m`} onClick={() => archiveOne(c)} title="Deactivate (archive)" className="p-1.5 hover:bg-amber-50 rounded"><Archive size={14} className="text-amber-700" /></button>}
                  <button data-testid={`edit-client-${c.id}-m`} onClick={() => edit(c)} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil size={14} /></button>
                  <button data-testid={`delete-client-${c.id}-m`} onClick={() => remove(c)} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 size={14} className="text-rose-600" /></button>
                </div>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
              {c.phone ? <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Phone</span><div className="tabular truncate">{c.phone}</div></div> : null}
              {c.email ? <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Email</span><div className="truncate">{c.email}</div></div> : null}
              {c.gstin ? <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">GSTIN</span><div className="tabular truncate">{c.gstin}</div></div> : null}
              {c.pan ? <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">PAN</span><div className="tabular truncate">{c.pan}</div></div> : null}
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-2">
              <a data-testid={`portal-open-${c.id}-m`} href={portalUrl(c)} target="_blank" rel="noreferrer" className="text-[10px] font-semibold uppercase tracking-widest underline decoration-dotted flex-1">Open portal</a>
              <Link data-testid={`client-bills-${c.id}-m`} to={`/clients/${c.id}/bills`} className="p-1.5 hover:bg-zinc-100 rounded"><Receipt size={14} /></Link>
              <button data-testid={`portal-copy-${c.id}-m`} onClick={() => copyPortal(c)} className="p-1.5 hover:bg-zinc-100 rounded"><LinkIcon size={14} /></button>
              {isAdmin ? <button data-testid={`portal-rotate-${c.id}-m`} onClick={() => rotatePortal(c)} className="p-1.5 hover:bg-amber-50 rounded"><RefreshCw size={14} className="text-amber-800" /></button> : null}
            </div>
          </div>
        ))}
        {!visible.length && <Empty q={q} category={category} archived={archived} />}
      </div>

      {/* Desktop: table */}
      <div className="hidden sm:block border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200">
              {isAdmin ? (
                <th className="px-3 py-2.5 w-8">
                  <button data-testid="select-all-clients" onClick={toggleAll} aria-label="Select all">
                    {allSel ? <CheckSquare size={15} /> : <Square size={15} className="text-zinc-400" />}
                  </button>
                </th>
              ) : null}
              {["Client", "Services", "Contact Person", "Phone", "Email", "GSTIN", "PAN", ...(vault.allowed ? ["GST Login"] : []), "Portal", ""].map(h => (
                <th key={h} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="stripe-rows">
            {visible.map(c => (
              <tr key={c.id} data-testid={`client-row-${c.id}`} className={`border-t border-zinc-100 ${selected.includes(c.id) ? "bg-zinc-50" : ""}`}>
                {isAdmin ? (
                  <td className="px-3 py-2.5">
                    <button data-testid={`select-client-${c.id}`} onClick={() => toggleSel(c.id)} aria-label={`Select ${c.name}`}>
                      {selected.includes(c.id) ? <CheckSquare size={15} /> : <Square size={15} className="text-zinc-400" />}
                    </button>
                  </td>
                ) : null}
                <td className="px-3 py-2.5 font-medium">{c.name}{archived ? <ArchiveMeta c={c} /> : null}</td>
                <td className="px-3 py-2.5"><CatChips c={c} /></td>
                <td className="px-3 py-2.5">{c.contact_person}</td>
                <td className="px-3 py-2.5 tabular">{c.phone}</td>
                <td className="px-3 py-2.5">{c.email}</td>
                <td className="px-3 py-2.5 tabular text-xs">{c.gstin}</td>
                <td className="px-3 py-2.5 tabular text-xs">{c.pan}</td>
                {vault.allowed ? (
                  <td className="px-3 py-2.5">
                    {isGstClient(c) ? (
                      <button data-testid={`gst-vault-btn-${c.id}`} onClick={() => setVaultClient(c)} title="GST portal login" className="hover:opacity-80">
                        <VaultBadge status={vaultStatus(c)} daysLeft={vault.items[c.id]?.days_left} tid={`gst-vault-badge-${c.id}`} />
                      </button>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                ) : null}
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    <a data-testid={`portal-open-${c.id}`} href={portalUrl(c)} target="_blank" rel="noreferrer" title={portalUrl(c) || "No portal token"} className="text-xs font-semibold underline decoration-dotted text-zinc-800 hover:text-zinc-900 truncate max-w-[140px]">Open portal</a>
                    <button data-testid={`portal-copy-${c.id}`} aria-label={`Copy portal link for ${c.name}`} onClick={() => copyPortal(c)} title="Copy portal link" className="p-1 hover:bg-zinc-100 rounded"><LinkIcon size={14} /></button>
                    {isAdmin ? <button data-testid={`portal-rotate-${c.id}`} aria-label={`Rotate portal link for ${c.name}`} onClick={() => rotatePortal(c)} title="Rotate portal link" className="p-1 hover:bg-amber-50 rounded"><RefreshCw size={14} className="text-amber-800" /></button> : null}
                  </div>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <Link data-testid={`client-bills-${c.id}`} to={`/clients/${c.id}/bills`} title="View client's sell bills" className="p-1 inline-block hover:bg-zinc-100 rounded"><Receipt size={15} /></Link>
                  <Link data-testid={`client-inventory-${c.id}`} to={`/clients/${c.id}/inventory`} title="Client's items & stock" className="p-1 inline-block hover:bg-zinc-100 rounded"><Package size={15} /></Link>
                  <Link data-testid={`client-purchases-${c.id}`} to={`/clients/${c.id}/purchases`} title="Client's purchases & expenses" className="p-1 inline-block hover:bg-zinc-100 rounded"><Truck size={15} /></Link>
                  <Link data-testid={`client-reports-${c.id}`} to={`/clients/${c.id}/reports`} title="Client's accounting reports" className="p-1 inline-block hover:bg-zinc-100 rounded"><BarChart3 size={15} /></Link>
                  {isAdmin ? (<>
                    {archived ? <button data-testid={`restore-client-${c.id}`} onClick={() => restoreOne(c)} title="Restore to active list" className="p-1 hover:bg-emerald-50 rounded"><ArchiveRestore size={15} className="text-emerald-700" /></button>
                      : <button data-testid={`archive-client-${c.id}`} onClick={() => archiveOne(c)} title="Deactivate (move to Archived)" className="p-1 hover:bg-amber-50 rounded"><Archive size={15} className="text-amber-700" /></button>}
                    <button data-testid={`edit-client-${c.id}`} onClick={() => edit(c)} className="p-1 hover:bg-zinc-100 rounded"><Pencil size={15} /></button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} className="p-1 hover:bg-rose-50 rounded"><Trash2 size={15} className="text-rose-600" /></button>
                  </>) : null}
                </td>
              </tr>
            ))}
            {!visible.length && <tr><td colSpan={(isAdmin ? 10 : 9) + (vault.allowed ? 1 : 0)}><Empty q={q} category={category} archived={archived} /></td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight text-2xl">{editId ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="client-name-input" />
            <Field label="Contact person" v={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} tid="client-contact-input" />
            <Field label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="client-phone-input" />
            <Field label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="client-email-input" />
            <Field label="GSTIN" v={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} tid="client-gstin-input" />
            <Field label="PAN" v={form.pan} onChange={(v) => setForm({ ...form, pan: v })} tid="client-pan-input" />
            <div className="col-span-2">
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Service category</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-testid="client-category-options">
                {CLIENT_CATEGORIES.map(c => {
                  const on = form.categories.includes(c.key);
                  return (
                    <button type="button" key={c.key} data-testid={`client-cat-${c.key}`} onClick={() => toggleCat(c.key)}
                      className={`text-left text-xs px-3 py-2 border transition-colors ${on ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {form.categories.includes("other") ? (
              <div className="col-span-2">
                <Field label="Nature of other service (e.g. TDS, ROC, Audit)" v={form.other_services} onChange={(v) => setForm({ ...form, other_services: v })} tid="client-other-services-input" />
              </div>
            ) : null}
            <div className="col-span-2">
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Notes</Label>
              <Textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="client-save-btn" onClick={save} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tab({ active, onClick, label, count, tid }) {
  return (
    <button data-testid={tid} onClick={onClick}
      className={`whitespace-nowrap text-xs font-semibold px-3 py-2 border-b-2 transition-colors ${active ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
      {label}{count != null ? <span className={`ml-1.5 tabular text-[10px] px-1.5 py-0.5 ${active ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700"}`}>{count}</span> : null}
    </button>
  );
}

function Chip({ active, onClick, label, tid }) {
  return (
    <button data-testid={tid} onClick={onClick}
      className={`text-[11px] px-2.5 py-1 border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}>
      {label}
    </button>
  );
}

function CatChips({ c }) {
  const cats = c.categories || [];
  if (!cats.length) return <span className="text-[10px] text-zinc-400 italic">Untagged</span>;
  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {cats.map(k => (
        <Link key={k} to={`/clients?category=${k}`} data-testid={`row-cat-${c.id}-${k}`} className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-700 hover:bg-zinc-900 hover:text-white transition-colors">
          {CATEGORY_SHORT[k]}{k === "other" && c.other_services ? ` · ${c.other_services}` : ""}
        </Link>
      ))}
    </div>
  );
}

function Empty({ q, category, archived }) {
  return (
    <div data-testid="clients-empty" className="text-center py-12 text-sm text-zinc-500">
      {q ? `No clients match “${q}”.` : archived ? "No archived clients. Use the archive icon on a client (or select several and press Archive) to deactivate them." : category ? `No clients tagged ${CATEGORY_LABEL[category]} yet.` : "No clients yet."}
    </div>
  );
}

function Field({ label, v, onChange, tid }) {
  return (
    <div>
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">{label}</Label>
      <Input data-testid={tid} value={v} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
